
import { addNamespace } from '@/app/shared/utils/NamespaceHelper'
import {
  postCategory as postCategoryService,
  updateLevelName as updateLevelNameService,
  createProductCategoryClass as createProductCategoryClassService,
  deleteProductCategory as deleteProductCategoryService
} from '@/app/admin/Services'

const NAMESPACE = 'admin/category/'

const _types = {
  getters: {
    GET_PRODUCT_CATEGORIES: 'GET_PRODUCT_CATEGORIES'
  },
  actions: {
    ADD_CATEGORY: 'ADD_CATEGORY',
    UPDATE_LEVEL_NAME: 'UPDATE_LEVEL_NAME',
    CREATE_CLASS: 'CREATE_CLASS',
    DELETE_CATEGORY: 'DELETE_CATEGORY'
  },
  mutations: {
    ADD_CATEGORY: 'ADD_CATEGORY',
    UPDATE_LEVEL_NAME: 'UPDATE_LEVEL_NAME',
    ADD_CLASS: 'ADD_CLASS',
    REMOVE_CATEGORIES: 'REMOVE_CATEGORIES',
    RESTORE_CATEGORIES: 'RESTORE_CATEGORIES'
  }
}

export const Types = addNamespace(NAMESPACE, _types)

// Deleting a category takes its subtree with it: the API removes the descendants
// server-side, so the local state has to drop them too or the tree keeps orphans.
const collectCategoryRemovals = (allClasses, categoryId) => {
  for (const classData of allClasses) {
    const levelList = classData.levelList || []
    const rootLevelIndex = levelList.findIndex(level => (level.options || []).some(option => option.id === categoryId))
    if (rootLevelIndex === -1) continue

    const removals = []
    let idsToRemove = [categoryId]

    for (let levelIndex = rootLevelIndex; levelIndex < levelList.length && idsToRemove.length > 0; levelIndex++) {
      const level = levelList[levelIndex]
      const options = level.options || []
      const matches = levelIndex === rootLevelIndex
        ? options.filter(option => option.id === categoryId)
        : options.filter(option => idsToRemove.includes(option.parent_id))

      matches.forEach(option => {
        removals.push({
          classId: classData.id,
          levelId: level.levelId || level.id,
          optionIndex: options.indexOf(option),
          category: option
        })
      })

      idsToRemove = matches.map(option => option.id)
    }

    return removals
  }

  return []
}

const findLevelOptions = (rootState, classId, levelId) => {
  const allClasses = rootState.desktopParameterConfig?.productCategoryFilters || []
  const classTarget = allClasses.find(classData => classData.id === classId)
  const levelTarget = classTarget?.levelList.find(level => (level.levelId || level.id) === levelId)
  return levelTarget?.options || null
}

const Category = {
  namespaced: true,
  getters: {
    [_types.getters.GET_PRODUCT_CATEGORIES]: (state, getters, rootState) => {
      return rootState.desktopParameterConfig?.productCategoryFilters
    }
  },
  actions: {
    async [_types.actions.ADD_CATEGORY] ({ commit, rootState }, category) {
      const newCategory = await postCategoryService(category)
      commit(_types.mutations.ADD_CATEGORY, { rootState, newCategory, levelId: category.levelId })
    },
    async [_types.actions.UPDATE_LEVEL_NAME] ({ commit, rootState }, { levelId, name }) {
      await updateLevelNameService(levelId, name)
      commit(_types.mutations.UPDATE_LEVEL_NAME, { rootState, levelId, newName: name })
    },
    async [_types.actions.DELETE_CATEGORY] ({ commit, rootState }, { categoryId }) {
      const allClasses = rootState.desktopParameterConfig?.productCategoryFilters || []
      const removals = collectCategoryRemovals(allClasses, categoryId)

      // Optimistic delete: the rows disappear before the request resolves and come
      // back untouched (same level, same position) if the API rejects the deletion.
      commit(_types.mutations.REMOVE_CATEGORIES, { rootState, removals })
      try {
        return await deleteProductCategoryService(categoryId)
      } catch (error) {
        commit(_types.mutations.RESTORE_CATEGORIES, { rootState, removals })
        throw error
      }
    },
    async [_types.actions.CREATE_CLASS] ({ commit, rootState }, className) {
      const response = await createProductCategoryClassService(className)
      commit(_types.mutations.ADD_CLASS, { rootState, classData: response })
      return response
    }
  },
  mutations: {
    [_types.mutations.ADD_CATEGORY] (_, { rootState, newCategory, levelId }) {
      const classTarget = rootState.desktopParameterConfig.productCategoryFilters.find(_class => _class.id === newCategory.class_id)
      const levelTarget = classTarget.levelList.find(level => level.levelId === levelId)
      levelTarget.options.push({ ...newCategory })
    },
    [_types.mutations.UPDATE_LEVEL_NAME] (_, { rootState, levelId, newName }) {
      const allClasses = rootState.desktopParameterConfig.productCategoryFilters
      for (const classData of allClasses) {
        const level = classData.levelList.find(level => level.id === levelId)
        if (level) {
          level.description = newName
          break
        }
      }
    },
    [_types.mutations.REMOVE_CATEGORIES] (_, { rootState, removals }) {
      removals.forEach(({ classId, levelId, category }) => {
        const options = findLevelOptions(rootState, classId, levelId)
        if (!options) return
        const currentIndex = options.findIndex(option => option.id === category.id)
        if (currentIndex > -1) options.splice(currentIndex, 1)
      })
    },
    [_types.mutations.RESTORE_CATEGORIES] (_, { rootState, removals }) {
      // Restored back to front so every original index is still valid.
      removals.slice().reverse().forEach(({ classId, levelId, optionIndex, category }) => {
        const options = findLevelOptions(rootState, classId, levelId)
        if (!options) return
        if (options.some(option => option.id === category.id)) return
        options.splice(Math.min(optionIndex, options.length), 0, category)
      })
    },
    [_types.mutations.ADD_CLASS] (_, { rootState, classData }) {
      // Create the new class structure based on the API response
      const newClass = {
        id: classData.class_id,
        class: classData.class_name,
        levelList: [
          {
            id: classData.level_id,
            levelId: classData.level_id,
            description: classData.level_name,
            options: []
          }
        ]
      }

      // Add the new class to the productCategoryFilters array
      if (!rootState.desktopParameterConfig) {
        rootState.desktopParameterConfig = {}
      }
      if (!rootState.desktopParameterConfig.productCategoryFilters) {
        rootState.desktopParameterConfig.productCategoryFilters = []
      }

      rootState.desktopParameterConfig.productCategoryFilters.push(newClass)
    }
  }
}

export default Category



// WEBPACK FOOTER //
// ./src/app/admin/store/Category.js